import {useCallback, useMemo} from 'react';
import cx from 'classnames';
import {ChevronIcon} from '../../../../../svg/ChevronIcon';
import {PlusIcon} from '../../../../../svg/PlusIcon';
import {MinusIcon} from '../../../../../svg/MinusIcon';
import {Link} from '../../../../../atoms/Link';
import {NewCategoryTag} from '../../../NewCategoryTag/NewCategoryTag';
import {Typography} from '../../../../../atoms/Typography';
import {Button} from '../../../../../atoms/Button';

import './CategoryItem.sass';

export const CategoryItem = ({
  category,
  title,
  linkTabIndex,
  level,
  allHref,
  setLevel1Category,
  setNavLevel,
  isExpanded,
  onToggleExpand,
}) => {
  const {
    href,
    dataQaId,
    new: newTag,
    hideSubCategories,
    name,
    subCategories,
    categories,
  } = category;

  const navigateToCategory = useCallback(() => {
    setLevel1Category(category);
    setNavLevel(1);
  }, [category, setLevel1Category, setNavLevel]);

  const toggleExpand = useCallback(() => {
    if (onToggleExpand) {
      onToggleExpand();
    }
  }, [onToggleExpand]);

  const newHref = useMemo(() => {
    if (!href) return '';
    if (href.startsWith('https://')) return href;
    const url = new URL(href, window.location.origin);
    return url.toString();
  }, [href]);

  const categoryId = title.toLowerCase().replaceAll(' ', '-');
  const hasSubcategories =
    !hideSubCategories && (subCategories?.length > 0 || categories?.length > 0);

  // For level 0 (main categories), we always want to navigate
  // For level 1 (subcategories), we want to toggle if it has subcategories, otherwise link
  const isLink = level === 0 ? hideSubCategories : !hasSubcategories;
  const isToggle = level === 1 && hasSubcategories;
  const isNavigate = level === 0 && !hideSubCategories;

  const renderSubcategories = () => (
    <div
      className={cx('category-item__subcategories', {
        'category-item__subcategories--expanded': isExpanded,
      })}
    >
      {categories?.map((subcat) => (
        <Link
          key={subcat.name}
          href={subcat.href}
          data-qa-id={subcat.dataQaId || `${subcat.name}_subcategoryLink`}
          className="category-item__nav-link category-item__subcategory-link"
          as="transparent"
        >
          <Typography variant="paragraph2">
            {subcat.display_name}
            {subcat.new ? <NewCategoryTag /> : null}
          </Typography>
        </Link>
      ))}
    </div>
  );

  if (isLink || allHref) {
    return (
      <Link
        href={newHref || allHref}
        data-qa-id={dataQaId || `${name}_subcategoryLink`}
        className="category-item__nav-link"
        as="transparent"
        variant="large"
      >
        <Typography
          variant={allHref ? 'label2' : 'paragraph2'}
          color={allHref ? 'coin' : 'charcoal'}
        >
          {title}
          {newTag ? <NewCategoryTag /> : null}
        </Typography>
      </Link>
    );
  }

  if (isToggle) {
    return (
      <div className="category-item__container">
        <Button
          className="category-item__nav-link"
          onClick={toggleExpand}
          tabIndex={linkTabIndex}
          data-qa-id={`${categoryId}_toggle`}
          aria-expanded={isExpanded}
          variant="transparent"
        >
          <Typography
            variant="label2"
            data-qa-id={dataQaId}
            color="coin"
            key={`${categoryId}_link`}
          >
            {title}
            {newTag ? <NewCategoryTag /> : null}
          </Typography>

          <span className="category-item__icon">
            {isExpanded ? <MinusIcon ratio={0.5} /> : <PlusIcon ratio={0.5} />}
          </span>
        </Button>
        {hasSubcategories && renderSubcategories()}
      </div>
    );
  }

  if (isNavigate) {
    return (
      <Button
        className="category-item__nav-link"
        onClick={navigateToCategory}
        tabIndex={linkTabIndex}
        data-qa-id={`${categoryId}_navigate`}
        variant="transparent"
      >
        <Typography
          variant="paragraph2"
          className="category-item__nav-link-inner"
          data-qa-id={dataQaId}
          key={`${categoryId}_link`}
        >
          {title}
          {newTag ? <NewCategoryTag /> : null}
        </Typography>
        <ChevronIcon ratio={0.5} direction="right" />
      </Button>
    );
  }

  return null;
};
